import { assertValidLngLatBounds } from "../geo/bounds.ts";
import { distanceMeters } from "../geo/distance.ts";
import { isValidLngLat } from "../geo/validation.ts";
import type { LngLat, LngLatBounds } from "../geo/types.ts";

// Search a bit beyond the visible viewport so small pans do not immediately
// invalidate the current result set.
const SEARCH_AREA_EXPANSION = 1.1;
// Mark results stale after a meaningful camera move or zoom change, while
// keeping the old results available until the user reruns the search.
const MIN_STALE_VISIBLE_AREA_RATIO = 0.6;
const MAX_STALE_VISIBLE_AREA_RATIO = 1.6;

export interface PoiSearchCamera {
  center: LngLat;
  zoom: number;
  visibleBounds: LngLatBounds;
}

export interface PoiSearchArea {
  center: LngLat;
  bounds: LngLatBounds;
  // Visible viewport at search time, used only for later stale checks.
  visibleBounds: LngLatBounds;
  zoom: number;
}

export function createPoiSearchArea(camera: PoiSearchCamera): PoiSearchArea {
  if (!isValidLngLat(camera.center)) {
    throw new RangeError(`Invalid POI search center: ${camera.center}`);
  }
  const visibleBounds = validBounds(
    camera.visibleBounds,
    "Invalid POI search visible bounds",
  );

  return {
    center: camera.center,
    bounds: expandBounds(visibleBounds, camera.center, SEARCH_AREA_EXPANSION),
    visibleBounds,
    zoom: camera.zoom,
  };
}

export function isPoiSearchStale(
  area: PoiSearchArea,
  camera: PoiSearchCamera,
): boolean {
  const visibleBounds = validBounds(
    camera.visibleBounds,
    "Invalid POI search visible bounds",
  );
  if (!containsBounds(area.bounds, visibleBounds)) {
    return true;
  }

  const visibleAreaRatio = boundsArea(visibleBounds) /
    boundsArea(area.visibleBounds);
  return visibleAreaRatio < MIN_STALE_VISIBLE_AREA_RATIO ||
    visibleAreaRatio > MAX_STALE_VISIBLE_AREA_RATIO;
}

function validBounds(bounds: LngLatBounds, label: string): LngLatBounds {
  assertValidLngLatBounds(bounds, label);
  return bounds;
}

function expandBounds(
  bounds: LngLatBounds,
  center: LngLat,
  factor: number,
): LngLatBounds {
  const [lng, lat] = center;

  let west = lng - (lng - bounds.west) * factor;
  let east = lng + (bounds.east - lng) * factor;
  if (west < -180 || east > 180) {
    west = -180;
    east = 180;
  }

  return {
    west,
    south: clampLatitude(lat - (lat - bounds.south) * factor),
    east,
    north: clampLatitude(lat + (bounds.north - lat) * factor),
  };
}

function containsBounds(outer: LngLatBounds, inner: LngLatBounds): boolean {
  return outer.west <= inner.west &&
    outer.south <= inner.south &&
    outer.east >= inner.east &&
    outer.north >= inner.north;
}

function boundsArea(bounds: LngLatBounds): number {
  const midLat = (bounds.south + bounds.north) / 2;
  const widthMeters = distanceMeters(
    [bounds.west, midLat],
    [bounds.east, midLat],
  );
  const midLng = (bounds.west + bounds.east) / 2;
  const heightMeters = distanceMeters(
    [midLng, bounds.south],
    [midLng, bounds.north],
  );
  return widthMeters * heightMeters;
}

function clampLatitude(lat: number): number {
  return Math.min(Math.max(lat, -90), 90);
}
